import React, { useState, useEffect } from "react";
import client from "../../lib/api";
import { mediaSrc } from "../../lib/media";
import { STAGES, fmtGbp } from "../../mock";
import { CitySelect } from "../../components/CitySelect";
import { useCities } from "../../hooks/useCities";
import { ScoreRing, StatusBadge } from "../../components/widgets";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Search, MapPin, BedDouble, Bath, TrendingUp, Sparkles, Filter,
  Plus, RefreshCw, Link2, Loader2, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LangContext";
import PropertyDetail from "./PropertyDetail";
import { TopFive } from "../../components/TopFive";
import { CardQuickActions } from "../../components/CardQuickActions";
import { SwipeDeck } from "../../components/SwipeDeck";
import { RankBadge, DisqualifiedBadge } from "../../components/DealControls";
import { PropertyForm, EMPTY_PROPERTY, formToPayload } from "../../components/PropertyForm";
import { PropertyFilters } from "../../components/PropertyFilters";
import { PropertyMap } from "../../components/Maps";
import { useLocations } from "../../hooks/useLocations";
import { useAuth } from "../../context/AuthContext";
import { EMPTY_FILTERS, applyFilters, titleBadge, statusBadge } from "../../lib/catalog";
import { FileText, Map as MapIcon, LayoutGrid, CheckCircle2, XCircle } from "lucide-react";

const AREAS = [
  "Medina", "Gueliz", "Hivernage", "Palmeraie", "Kasbah", "Bahia",
  "Mouassine", "Laksour", "Sidi Mimoun", "Agdal", "Targa", "Semlalia", "Annakhil",
];

const EMPTY_FORM = { title: "", address: "", district: "", city: "Marrakech", beds: "", baths: "", size: "", rentMad: "" };

export default function Properties() {
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [dispFilter, setDispFilter] = useState("active");
  const [swipeOpen, setSwipeOpen] = useState(false);
  const [detailId, setDetailId] = useState(() => sessionStorage.getItem("mb_detail") || null);

  const openDetail = (pid) => { sessionStorage.setItem("mb_detail", pid); setDetailId(pid); };
  const closeDetail = () => { sessionStorage.removeItem("mb_detail"); setDetailId(null); };

  const [addOpen, setAddOpen] = useState(false);
  const { cities: cityList, addCity, removeCity } = useCities();
  const [importOpen, setImportOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_PROPERTY });
  const [lf, setLf] = useState({ ...EMPTY_FILTERS });
  const [view, setView] = useState("list");
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const loc = useLocations();
  const { user } = useAuth();
  const { t } = useLang();
  const isSuper = user?.role === "super_admin";
  const review = async (p, action) => {
    try { const r = await client.post(`/properties/${p.id}/review`, { action, reason: rejectReason }); onChanged(r.data); setRejecting(null); setRejectReason(""); toast.success(action === "approve" ? "Approved — students can see it now" : "Rejected"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
  };
  const [importUrl, setImportUrl] = useState("");

  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const [resP, resA] = await Promise.all([client.get("/properties"), client.get("/agents")]);
      setItems(resP.data);
      setAgents(resA.data);
    } catch (e) {
      toast.error("Couldn't load properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.agentId) { toast.error(t("Pick the agent who sent this listing")); return; }
    if (!form.propertyType) { toast.error(t("Pick a property type")); return; }
    setSaving(true);
    try {
      const payload = formToPayload({ ...form, title: form.title || `${form.propertyType} · ${loc.quarterName(form.quarterId) || loc.areaName(form.areaId) || "Marrakech"}` });
      const res = await client.post("/properties", { ...payload, status: "Warm" });
      setItems((prev) => [res.data, ...prev]);
      setForm({ ...EMPTY_PROPERTY });
      setAddOpen(false);
      toast.success("Property saved", { description: `${res.data.title} added to your pipeline.` });
    } catch (err) {
      toast.error("Couldn't save property", { description: err?.response?.data?.detail });
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importUrl.trim()) {
      toast.error("Paste a listing URL");
      return;
    }
    setImporting(true);
    try {
      const res = await client.post("/properties/import", { url: importUrl.trim() });
      setImportUrl("");
      setImportOpen(false);
      if (res.data.duplicate) {
        setItems((prev) => (prev.some((x) => x.id === res.data.id) ? prev : [res.data, ...prev]));
        toast.info("Already in your pipeline", { description: `${res.data.title} was imported before.` });
      } else if (res.data.refreshed) {
        setItems((prev) => prev.map((x) => (x.id === res.data.id ? res.data : x)));
        toast.success("Listing refreshed", { description: `${res.data.title} — details updated from the source.` });
      } else {
        setItems((prev) => [res.data, ...prev]);
        toast.success("Listing imported", { description: res.data.title });
      }
    } catch (err) {
      toast.error("Import failed", { description: err?.response?.data?.detail });
    } finally {
      setImporting(false);
    }
  };

  const priceLabel = (p) => {
    if (p.listingType === "rental" && p.rentMad) return `${p.rentMad.toLocaleString()} MAD/mo`;
    if (p.priceMad) return `${p.priceMad.toLocaleString()} MAD`;
    if (p.askGbp) return fmtGbp(p.askGbp);
    return "—";
  };

  const filtered = items.filter((p) => {
    const q = query.toLowerCase();
    const matchQ = !q || p.title.toLowerCase().includes(q) || (p.district || "").toLowerCase().includes(q);
    const matchS = stageFilter === "all" || p.stage === stageFilter;
    const matchC = cityFilter === "all" || p.city === cityFilter || !p.city;
    const out = p.disposition === "disqualified";
    const matchD = dispFilter === "all" || (dispFilter === "disqualified" ? out : !out);
    return matchQ && matchS && matchC && matchD;
  });
  const filteredAll = applyFilters(filtered, lf);
  const pendingQueue = items.filter((p) => p.listingStatus === "Pending review");
  const disqualifiedCount = items.filter((p) => p.disposition === "disqualified").length;
  const onChanged = (updated) => setItems((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));

  if (detailId) {
    return (
      <PropertyDetail
        id={detailId}
        agents={agents}
        onBack={() => { closeDetail(); load(true); }}
        onChanged={onChanged}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Header + toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-white">{t("Properties")}</h2>
          <p className="text-xs text-muted-foreground">{items.length} {t("riads & units in your pipeline")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load(true)} title="Refresh"
            className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/[0.03] text-muted-foreground hover:text-white transition-colors">
            <RefreshCw className="h-4 w-4" />
          </button>
          <Button variant="outline" onClick={() => setSwipeOpen(true)} data-testid="swipe-mode-btn" title="Swipe right to shortlist, left to disqualify"
            className="h-10 border-amber-400/30 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20">
            <Layers className="h-4 w-4" /> {t("Swipe")}
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="mubawab-import-btn"
            className="h-10 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">
            <Link2 className="h-4 w-4" /> {t("Import link")}
          </Button>
          <Button onClick={() => setAddOpen(true)} data-testid="add-property-btn" className="h-10 bg-brand-red hover:bg-brand-redDark text-white">
            <Plus className="h-4 w-4" /> {t("Add")}
          </Button>
        </div>
      </div>

      {/* Search + filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("Search by name or district…")}
            className="h-11 bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
        </div>
        <Select value={cityFilter} onValueChange={setCityFilter}>
          <SelectTrigger className="h-11 w-full sm:w-[180px] border-white/12 bg-white/[0.03] text-white" data-testid="properties-city-filter">
            <MapPin className="h-4 w-4 text-muted-foreground" /><SelectValue />
          </SelectTrigger>
          <SelectContent className="glass-strong border-white/10">
            <SelectItem value="all">{t("All cities")}</SelectItem>
            {(cityList || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          <Filter className="h-4 w-4 shrink-0 text-muted-foreground" />
          {[{ id: "all", label: "All" }, ...STAGES].map((s) => (
            <button key={s.id} onClick={() => setStageFilter(s.id)}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                stageFilter === s.id ? "border-brand-red bg-brand-red/15 text-brand-red" : "border-white/10 text-muted-foreground hover:text-white"
              }`}>{t(s.label)}</button>
          ))}
        </div>
      </div>

      {isSuper && pendingQueue.length > 0 && (
        <section data-testid="pending-queue" className="rounded-2xl border border-brand-red/25 bg-brand-red/[0.05] p-4">
          <h3 className="font-display text-base font-semibold text-white">{t("Pending review")} · {pendingQueue.length}</h3>
          <p className="text-xs text-muted-foreground">{t("Sent by agents through their links. Hidden from students until you approve.")}</p>
          <div className="mt-3 grid gap-2">
            {pendingQueue.map((p) => (
              <div key={p.id} data-testid={`pending-${p.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.07] bg-card/60 p-3">
                <img src={mediaSrc(p.image)} alt="" className="h-14 w-20 rounded-lg object-cover" />
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-white">{p.title}</p><p className="text-xs text-muted-foreground">{p.propertyType} · {p.district} · {agents.find((a) => a.id === p.agentId)?.name || "Agent"} · {priceLabel(p)}</p></div>
                <Button size="sm" variant="outline" onClick={() => openDetail(p.id)} data-testid={`pending-edit-${p.id}`} className="h-9 border-white/12 bg-white/[0.03] text-white">{t("Edit")}</Button>
                <Button size="sm" onClick={() => review(p, "approve")} data-testid={`pending-approve-${p.id}`} className="h-9 bg-brand-green text-white hover:bg-brand-green/90"><CheckCircle2 className="h-4 w-4" /> {t("Approve")}</Button>
                <Button size="sm" variant="outline" onClick={() => setRejecting(p)} data-testid={`pending-reject-${p.id}`} className="h-9 border-brand-red/40 bg-brand-red/10 text-brand-red"><XCircle className="h-4 w-4" /> {t("Reject")}</Button>
              </div>
            ))}
          </div>
        </section>
      )}
      <PropertyFilters items={filtered} filters={lf} setFilters={setLf} locations={loc} agents={agents} />
      <div className="flex flex-wrap items-center gap-2" data-testid="disposition-filter">
        <div className="mr-2 flex rounded-lg border border-white/10 p-0.5">
          <button onClick={() => setView("list")} data-testid="view-list" className={`rounded-md px-2.5 py-1 text-xs ${view === "list" ? "bg-white/10 text-white" : "text-muted-foreground"}`}><LayoutGrid className="h-3.5 w-3.5" /></button>
          <button onClick={() => setView("map")} data-testid="view-map" className={`rounded-md px-2.5 py-1 text-xs ${view === "map" ? "bg-white/10 text-white" : "text-muted-foreground"}`}><MapIcon className="h-3.5 w-3.5" /></button>
        </div>
        {[["active", `${t("Active")} (${items.length - disqualifiedCount})`], ["disqualified", `${t("Disqualified")} (${disqualifiedCount})`], ["all", t("All")]].map(([k, l]) => (
          <button key={k} onClick={() => setDispFilter(k)} data-testid={`disposition-${k}`}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${dispFilter === k ? "border-brand-green/35 bg-brand-green/10 text-brand-green" : "border-white/12 bg-white/[0.03] text-muted-foreground hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {!loading && dispFilter === "active" && !query && <TopFive items={items} priceLabel={priceLabel} onOpen={openDetail} onChanged={onChanged} />}

      {!loading && view === "map" && (
        <div className="space-y-2" data-testid="map-view">
          <PropertyMap properties={filteredAll} onSelect={(p) => openDetail(p.id)} height="h-[520px]" />
          <p className="text-xs text-muted-foreground">{filteredAll.filter((p) => p.latitude).length} / {filteredAll.length} · {t("pins coloured by type (red Riad, green Villa, blue Apartment, amber Land) · tap a pin to open")}</p>
        </div>
      )}
      {loading ? (
        <div className="grid h-48 place-items-center">
          <div className="h-8 w-8 rounded-full border-2 border-brand-red border-t-transparent animate-spin" />
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {filteredAll.map((p) => {
            const stage = STAGES.find((s) => s.id === p.stage) || STAGES[0];
            return (
              <div key={p.id} role="button" tabIndex={0} onClick={() => openDetail(p.id)} onKeyDown={(e) => { if (e.key === "Enter") openDetail(p.id); }} data-testid={`property-card-${p.id}`}
                className={`group relative cursor-pointer overflow-hidden rounded-2xl border text-left transition-all hover:-translate-y-1 hover:border-white/15 ${p.disposition === "disqualified" ? "border-white/[0.04] bg-card/20 opacity-60 grayscale hover:opacity-90" : "border-white/[0.06] bg-card/40"}`}>
                <CardQuickActions property={p} onChanged={onChanged} onOpen={openDetail} />
                <div className="relative h-44 overflow-hidden">
                  <img src={mediaSrc(p.image)} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute left-3 top-3 flex items-center gap-1.5">
                    {p.disposition === "disqualified" ? <DisqualifiedBadge reason={p.disqualifyReason} /> : <StatusBadge status={p.status} />}
                    <RankBadge rank={p.disposition === "disqualified" ? 0 : p.rank} />
                    {p.titleStatus && <span data-testid="title-status-badge" className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${titleBadge(p.titleStatus)}`}>{t(p.titleStatus)}</span>}
                  </div>
                  <div className="absolute right-3 top-3 rounded-xl bg-black/50 p-1 backdrop-blur"><ScoreRing score={p.score} size={44} /></div>
                  <div className="absolute bottom-3 left-3 right-3">
                  <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                    {p.propertyType && <span className="rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white">{t(p.propertyType)}</span>}
                    {p.listingStatus && p.listingStatus !== "Available" && <span data-testid="listing-status-badge" className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusBadge(p.listingStatus)}`}>{t(p.listingStatus)}</span>}
                    {p.docCount > 0 && <span data-testid="doc-count-badge" className="inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white"><FileText className="h-3 w-3" /> {p.docCount} {t(p.docCount === 1 ? "doc" : "docs")}</span>}
                  </div>
                    <p className="font-display text-base font-semibold text-white">{p.title}</p>
                    <p className="flex items-center gap-1 text-xs text-white/70"><MapPin className="h-3 w-3" />{p.district || p.address || "Marrakech"}</p>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-display text-lg font-semibold text-white">{priceLabel(p)}</span>
                    {p.yield ? (
                      <span className="flex items-center gap-1 text-xs font-medium text-brand-green"><TrendingUp className="h-3.5 w-3.5" />{p.yield}% {t("yield")}</span>
                    ) : null}
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />{p.beds}</span>
                    <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{p.baths}</span>
                    {p.size ? <span>{p.size} m²</span> : null}
                    <span className="ml-auto inline-flex items-center gap-1" style={{ color: stage.color }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: stage.color }} />{stage.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full grid h-40 place-items-center rounded-2xl border border-dashed border-white/10 text-sm text-muted-foreground">
              {t("No properties here — add one or import a listing link.")}
            </div>
          )}
        </div>
      )}

      {swipeOpen && <SwipeDeck items={items} priceLabel={priceLabel} onChanged={onChanged} onClose={() => setSwipeOpen(false)} />}

      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) setRejecting(null); }}>
        <DialogContent className="max-w-md glass-strong border-white/10" data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl text-white">{t("Reject submission")} — {rejecting?.title}</DialogTitle><DialogDescription>{t("Reason (the agent will see it)")}</DialogDescription></DialogHeader>
          <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t("Reason (the agent will see it)")} data-testid="reject-reason" className="h-11 bg-input/60 border-white/10" />
          <Button onClick={() => review(rejecting, "reject")} data-testid="reject-confirm" className="h-11 w-full bg-brand-red text-white hover:bg-brand-redDark">{t("Confirm rejection")}</Button>
        </DialogContent>
      </Dialog>

      {/* New Property modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto glass-strong border-white/10" data-testid="add-property-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">{t("New Property")}</DialogTitle>
          </DialogHeader>
          <PropertyForm form={form} setForm={setForm} onSubmit={handleSave} submitting={saving} submitLabel={t("Save property")} />
        </DialogContent>
      </Dialog>

      {/* Import from Mubawab modal */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">{t("Import a listing")}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Paste a link from Mubawab, Avito, Sarouty, Airbnb, an agency site — anywhere. We'll pull the title, photos, videos, price and specs. Mubawab links also bring the agent across.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleImport} className="space-y-4 pt-1">
            <Input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://…" data-testid="mubawab-import-input"
              className="h-12 bg-input/60 border-white/10 focus-visible:ring-brand-red" />
            <Button type="submit" disabled={importing} data-testid="mubawab-import-submit" className="h-12 w-full bg-brand-red hover:bg-brand-redDark text-white font-medium">
              {importing ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Importing…</span> : "Import"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
